import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Shield, Users, Target, ChartLine, Globe, Handshake } from '@phosphor-icons/react';

export default function About() {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero */}
      <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#0066FF]/8 via-background to-background"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-[#0066FF]/10 border border-[#0066FF]/20 mb-6">
            <span className="text-xs font-medium text-[#0066FF] tracking-wide">ABOUT US</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-6" data-testid="about-heading">
            Protecting the future of{' '}
            <span className="text-[#0066FF]">digital finance</span>
          </h1>
          <p className="text-base lg:text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
            Financial Intelligence is a leading blockchain analytics company dedicated to combating cryptocurrency crime and fostering a secure digital financial ecosystem.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <Card className="p-8 bg-card border-border">
              <div className="w-12 h-12 rounded-xl bg-[#0066FF]/10 flex items-center justify-center text-[#0066FF] mb-6">
                <Target size={28} weight="duotone" />
              </div>
              <h2 className="font-heading text-2xl font-medium text-white mb-4">Our Mission</h2>
              <p className="text-sm text-slate-400 leading-relaxed">
                To provide world-class blockchain intelligence and investigation services that protect individuals, businesses, and governments from cryptocurrency fraud and illicit financial activities. We leverage cutting-edge technology and expert analysis to trace, investigate, and protect digital assets.
              </p>
            </Card>
            <Card className="p-8 bg-card border-border">
              <div className="w-12 h-12 rounded-xl bg-[#0066FF]/10 flex items-center justify-center text-[#0066FF] mb-6">
                <Globe size={28} weight="duotone" />
              </div>
              <h2 className="font-heading text-2xl font-medium text-white mb-4">Our Vision</h2>
              <p className="text-sm text-slate-400 leading-relaxed">
                To be the global standard in blockchain intelligence, creating a transparent and secure cryptocurrency ecosystem where digital assets are protected, compliance is seamless, and justice is served through advanced analytics and collaborative partnerships.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 lg:py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white mb-4">
              Why Choose Financial Intelligence
            </h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto">
              With years of experience and cutting-edge technology, we deliver results that matter.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Shield size={28} weight="duotone" />,
                title: 'Industry Expertise',
                description: 'Our team consists of former law enforcement officials, cybersecurity experts, and blockchain analysts with decades of combined experience.'
              },
              {
                icon: <ChartLine size={28} weight="duotone" />,
                title: 'Advanced Analytics',
                description: 'We utilize AI-powered blockchain analytics to trace transactions across 330+ blockchains and identify patterns invisible to the human eye.'
              },
              {
                icon: <Users size={28} weight="duotone" />,
                title: 'Global Network',
                description: 'Partnerships with exchanges, law enforcement agencies, and financial institutions worldwide enable effective asset tracing and protection.'
              },
              {
                icon: <Handshake size={28} weight="duotone" />,
                title: 'Client-Centric Approach',
                description: 'Every case is handled with the utmost confidentiality and dedication. We keep our clients informed at every step of the investigation.'
              },
              {
                icon: <Target size={28} weight="duotone" />,
                title: 'Proven Track Record',
                description: 'We have successfully traced and protected millions in digital assets for individuals and organizations across the globe.'
              },
              {
                icon: <Globe size={28} weight="duotone" />,
                title: 'Regulatory Compliance',
                description: 'Our solutions help businesses meet AML/KYC requirements and stay compliant with evolving cryptocurrency regulations worldwide.'
              }
            ].map((item, idx) => (
              <Card key={idx} className="p-6 bg-card border-border hover:border-[#0066FF]/30 transition-all duration-300" data-testid={`about-feature-${idx}`}>
                <div className="w-12 h-12 rounded-xl bg-[#0066FF]/10 flex items-center justify-center text-[#0066FF] mb-4">{item.icon}</div>
                <h3 className="font-heading text-base font-medium text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{item.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: '10+', label: 'Years of Experience' },
              { number: '500+', label: 'Cases Resolved' },
              { number: '$50M+', label: 'Assets Protected' },
              { number: '50+', label: 'Countries Served' },
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0066FF] mb-2">{stat.number}</p>
                <p className="text-sm text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
